'use client';

import { useCallback, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslations } from 'next-intl';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { PHONE_REGEX } from '@/constants';
import { useCreateOfficeInquiry } from '@/queris';
import { showToast } from '@/utils/showToast';

// Limits mirror OfficeInquiryRequestRo (POST /inquiry/office/{domain}/{officeId}).
const MAX_TEXT_LENGTH = 150;
const MAX_PHONE_LENGTH = 30;
const MAX_COMMENT_LENGTH = 2000;
const COOLDOWN_MS = 10000;

export interface BrokerContactFormValues {
  name: string;
  surname: string;
  email: string;
  phone: string;
  message: string;
  requestCall: boolean;
  gdprAccepted: boolean;
}

const FORM_INITIAL_VALUES: BrokerContactFormValues = {
  name: '',
  surname: '',
  email: '',
  phone: '',
  message: '',
  requestCall: false,
  gdprAccepted: false,
};

export const useBrokerContactForm = (officeId: number) => {
  const t = useTranslations();
  const [cooldown, setCooldown] = useState(false);

  const triggerCooldown = useCallback(() => {
    setCooldown(true);
    setTimeout(() => setCooldown(false), COOLDOWN_MS);
  }, []);

  const { mutate: createOfficeInquiry, isPending } = useCreateOfficeInquiry(
    () => showToast.success(t('housingCardDetails.form.emailSent')),
    () => toast.error(t('housingCardDetails.form.emailSendError')),
  );

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(t('form.required'))
      .max(MAX_TEXT_LENGTH, ({ max }) => t('form.maxLength', { max })),
    surname: Yup.string()
      .required(t('form.required'))
      .max(MAX_TEXT_LENGTH, ({ max }) => t('form.maxLength', { max })),
    email: Yup.string()
      .email(t('form.wrongEmail'))
      .required(t('form.required'))
      .max(MAX_TEXT_LENGTH, ({ max }) => t('form.maxLength', { max })),
    phone: Yup.string()
      .required(t('form.required'))
      .matches(PHONE_REGEX, t('form.wrongPhone'))
      .max(MAX_PHONE_LENGTH, ({ max }) => t('form.maxLength', { max })),
    message: Yup.string().max(MAX_COMMENT_LENGTH, ({ max }) => t('form.maxLength', { max })),
    gdprAccepted: Yup.boolean().oneOf([true]).required(t('form.required')),
  });

  const formik = useFormik<BrokerContactFormValues>({
    initialValues: FORM_INITIAL_VALUES,
    validationSchema,
    onSubmit: ({ message, ...values }, { resetForm }) => {
      createOfficeInquiry(
        {
          officeId,
          inquiryObj: {
            ...values,
            comment: message,
            sendEmail: true,
            sendWhatsapp: false,
          },
        },
        { onSuccess: () => resetForm() },
      );
      triggerCooldown();
    },
  });

  const fieldProps = (name: keyof BrokerContactFormValues) => ({
    ...formik.getFieldProps(name),
    error: !!formik.errors[name] && !!formik.touched[name],
    helperText: formik.touched[name] && formik.errors[name] ? (formik.errors[name] as string) : '',
  });

  return { formik, fieldProps, isDisabled: isPending || cooldown };
};
